import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';

interface InterestsSelectorProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  maxSelected?: number;
  className?: string;
}

export const InterestsSelector: React.FC<InterestsSelectorProps> = ({ selected, onChange, maxSelected = 5 }) => {
  const [search, setSearch] = useState('');
  const [allInterests, setAllInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchInterests = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('hobbies')
        .select('name')
        .order('name', { ascending: true });
      if (!error && data) {
        setAllInterests(data.map((item: any) => item.name));
      }
      setLoading(false);
    };
    fetchInterests();
  }, []);

  const filtered = allInterests.filter(
    (i) => i.toLowerCase().includes(search.toLowerCase()) && !selected.includes(i)
  );

  const handleAdd = (interest: string) => {
    if (!selected.includes(interest) && selected.length < maxSelected) {
      onChange([...selected, interest]);
      setSearch('');
    }
  };

  const handleRemove = (interest: string) => {
    onChange(selected.filter((i) => i !== interest));
  };

  const reachedMax = selected.length >= maxSelected;

  return (
    <div className="w-full max-w-full min-w-0">
      <input
        type="text"
        placeholder={loading ? 'Loading interests...' : 'Search or add interest...'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClick={() => setIsDropdownOpen(true)}
        onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
        className="mb-2 bg-white text-black placeholder:text-[#b0b0b0] border border-black rounded-md px-3 py-2 font-sans w-full max-w-full min-w-0 focus:outline-none"
        disabled={reachedMax || loading}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && search.trim() && !reachedMax) {
            handleAdd(search.trim());
          }
        }}
      />
      {isDropdownOpen && filtered.length > 0 && !reachedMax && !loading && (
        <div className="border rounded bg-white p-2 mb-2 max-h-32 overflow-y-auto">
          {filtered.map((interest) => (
            <div
              key={interest}
              className="cursor-pointer px-2 py-1 hover:bg-[#D72E16] hover:text-white rounded font-sans text-black"
              onClick={() => handleAdd(interest)}
            >
              {interest}
            </div>
          ))}
        </div>
      )}
      {isDropdownOpen && filtered.length === 0 && !reachedMax && !loading && (
        <div className="border rounded bg-white p-2 mb-2">
          <div
            className="cursor-pointer px-2 py-1 hover:bg-[#D72E16] hover:text-white rounded font-sans text-black"
            onClick={() => handleAdd(search.trim())}
          >
            Add "{search.trim()}"
          </div>
        </div>
      )}
      <div className="mb-2">
        <div className="flex flex-wrap gap-2 mb-2">
          {selected.map((interest) => (
            <span
              key={interest}
              className="inline-flex items-center rounded-full bg-red-100 text-red-700 border border-red-200 px-4 py-1 text-xs font-medium font-sans mr-2 mb-2"
            >
              {interest}
              <button
                type="button"
                onClick={() => handleRemove(interest)}
                className="ml-2 text-xs text-red-400 hover:text-red-700 focus:outline-none"
                aria-label={`Remove ${interest}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="text-xs text-[#b0b0b0] mt-1 font-sans">
          {selected.length}/{maxSelected} interests selected
        </div>
        {reachedMax && (
          <div className="text-xs text-red-500 mt-1 font-sans">You can select up to {maxSelected} interests only.</div>
        )}
      </div>
    </div>
  );
}; 